// HistorieChart.tsx — shared uPlot host for every TIME-SERIES view on the Historie page
// (Kurs, Wachstum, Drawdown, Rollierende Rendite p.a.): one series/N-series line(+fill)
// chart with the same money-axis gutter fix (measuredAxisSize), the same 2D wheel/pinch
// zoom (chartZoomPlugin) and the same "Zoom zurücksetzen" affordance every other uPlot
// chart in this app already has (SimulationChart/CompareChart/MonteCarloChart) — this
// file is the generalisation of that shared shape so HistorieView doesn't hand-roll a
// 4th near-identical uPlot options object. The calendar-year/Ausschüttungen/AGE bar
// views are hand-rolled SVG instead (HistorieBarChart.tsx) — categorical, not a
// continuous time axis, so they don't fit this component.
import { useCallback, useMemo, useState } from 'preact/hooks';
import type { AlignedData, Options } from 'uplot';
import { UplotBase } from './UplotBase.js';
import { zeroLinePlugin, verticalMarkersPlugin, chartZoomPlugin } from '../lib/uplot-plugins.js';
import type { ZoomState } from '../lib/uplot-plugins.js';
import { measuredAxisSize } from '../lib/uplotAxis.js';

export interface HistorieSeriesSpec {
  label: string;
  color: string;
  fill?: string;
  width?: number;
  dash?: number[];
  valueFmt: (v: number | null) => string;
}

export interface HistorieChartProps {
  /** Unix seconds, ascending — shared x-axis for every series below. */
  x: number[];
  /** One (number|null)[] per `specs` entry, index-aligned with `x`. */
  series: (number | null)[][];
  specs: HistorieSeriesSpec[];
  yTickFmt: (v: number) => string;
  height?: number;
  /** Dashed horizontal line at y=0 (Rollierende Rendite: p.a. can be negative). */
  showZeroLine?: boolean;
  /** Small dotted vertical markers (distribution ex-dates on the Kurs view). */
  markerXs?: number[];
  markerColor?: string;
  emptyMessage?: string;
}

export function HistorieChart({
  x,
  series,
  specs,
  yTickFmt,
  height = 240,
  showZeroLine,
  markerXs,
  markerColor,
  emptyMessage,
}: HistorieChartProps) {
  const [zoom, setZoom] = useState<ZoomState | null>(null);
  const data = useMemo((): AlignedData => [x, ...series] as unknown as AlignedData, [x, series]);

  const buildOptions = useCallback(
    (width: number): Options => {
      return {
        width,
        height,
        padding: [8, 8, 0, 0],
        legend: { show: specs.length > 1 },
        cursor: { drag: { x: true, y: true }, points: { size: 6 } },
        scales: {
          x: { time: true },
          // Always clamps 0 into the y-range, exactly like SimulationChart/CompareChart/
          // MonteCarloChart's own `scales.y.range` — NOT just cosmetic consistency: the
          // shared chartZoomPlugin's "is this chart currently zoomed" / "reset" logic
          // (lib/uplot-plugins.ts's yDataRange) itself always clamps to include 0, so a
          // y-range that DIDN'T would read as permanently "zoomed" (a real bug caught
          // while screenshotting: a tight, zero-excluding range made "Zoom zurücksetzen"
          // appear on first load and jump to a MORE zoomed-out view on click).
          y: { range: (_u, initMin, initMax) => [Math.min(0, initMin), Math.max(0, initMax)] },
        },
        axes: [
          {
            stroke: '#93a0b8',
            grid: { stroke: 'rgba(232,236,244,0.08)', width: 1 },
            ticks: { stroke: 'rgba(232,236,244,0.15)', width: 1 },
          },
          {
            stroke: '#93a0b8',
            grid: { stroke: 'rgba(232,236,244,0.08)', width: 1 },
            ticks: { stroke: 'rgba(232,236,244,0.15)', width: 1 },
            values: (_u, ticks) => ticks.map((t) => yTickFmt(t)),
            size: measuredAxisSize,
          },
        ],
        series: [
          { label: 'Datum' },
          ...specs.map((s) => ({
            label: s.label,
            stroke: s.color,
            width: s.width ?? 2,
            ...(s.fill !== undefined ? { fill: s.fill } : {}),
            ...(s.dash !== undefined ? { dash: s.dash } : {}),
            value: (_u: unknown, v: number | null) => s.valueFmt(v),
          })),
        ],
        plugins: [
          ...(showZeroLine ? [zeroLinePlugin()] : []),
          ...(markerXs && markerXs.length > 0 ? [verticalMarkersPlugin(() => markerXs, markerColor)] : []),
          chartZoomPlugin(setZoom),
        ],
      };
    },
    [specs, yTickFmt, height, showZeroLine, markerXs, markerColor],
  );

  if (x.length === 0) {
    return <div class="empty-state">{emptyMessage ?? 'Keine Daten vorhanden.'}</div>;
  }

  return (
    <div class="chart-wrap">
      <UplotBase data={data} buildOptions={buildOptions} height={height} />
      {zoom?.zoomed ? (
        <div class="chart-zoom-row">
          <button type="button" class="chart-zoom-reset" onClick={() => zoom.reset()}>
            Zoom zurücksetzen
          </button>
        </div>
      ) : null}
    </div>
  );
}
